import type {
  ExtendedResourceInvoice,
  SupabaseClient,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import {
  createInPredefinedListFilter,
  createListContainsFilter,
  createListFilter,
  createStringFilter,
  DataTableQueryBuilder,
  ensureColumnFilter,
} from "~/lib/utils/datatable-query-builder"
import type {
  UpdateResourceInvoiceData,
  UpdateResourceInvoiceStatusData,
} from "~/modules/resources/resource-invoices.schema"
import { QUERYABLE_RESOURCE_INVOICE_STATUSES } from "./resources.constants"

export const createResourceInvoicesService = (supabase: SupabaseClient) => {
  return new ResourceInvoicesService(supabase)
}

class ResourceInvoicesService {
  private log = logger.child({ module: "resource-invoices-service" })

  constructor(private readonly supabase: SupabaseClient) {}

  async getManyExtended({
    resourceUnitId,
    filters,
  }: {
    resourceUnitId?: string
    filters?: DataTableBaseSearch
  } = {}) {
    const builder = new DataTableQueryBuilder(this.supabase, {
      table: "extended_resource_invoices",
      searchField: "fts_resource_invoice",
      defaultSort: { field: "created_at", ascending: false },
      fieldMappings: { resource: "resource_first_name" },
      filters: {
        status: createInPredefinedListFilter(
          "status",
          QUERYABLE_RESOURCE_INVOICE_STATUSES,
        ),
        business_entity_unit_ids: createListFilter("business_entity_unit_id"),
        client_unit_ids: createListContainsFilter("client_unit_ids"),
        resource_unit_id: createStringFilter("resource_unit_id"),
        invoice_period_id: createStringFilter("invoice_period_id"),
      },
    })

    const filtersWithResource = resourceUnitId
      ? {
          ...filters,
          columnFilters: ensureColumnFilter(filters?.columnFilters, {
            id: "resource_unit_id",
            value: resourceUnitId,
          }),
        }
      : filters

    const { data, count } = await builder.execute<ExtendedResourceInvoice>({
      page: 1,
      page_size: 20,
      ...filtersWithResource,
    })

    return {
      invoices: data,
      count,
    }
  }

  async getOneExtended({ invoiceId }: { invoiceId: string }) {
    const { data, error } = await this.supabase
      .from("extended_resource_invoices")
      .select("*")
      .eq("id", invoiceId)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching resource invoice", { error })
      throw new Error(error.message)
    }

    return data
  }

  async create({
    businessEntityUnitId,
    resourceUnitId,
    currencyCode,
    invoicePeriodId,
    notes,
  }: {
    businessEntityUnitId: string
    resourceUnitId: string
    currencyCode: string
    invoicePeriodId?: string
    notes?: string
  }) {
    const { data: newInvoiceId, error } = await this.supabase.rpc(
      "create_resource_invoice",
      {
        p_business_entity_unit_id: businessEntityUnitId,
        p_resource_unit_id: resourceUnitId,
        p_currency_code: currencyCode,
        p_invoice_period_id: invoicePeriodId,
        p_notes: notes,
      },
    )

    if (error) {
      this.log.error("Error creating resource invoice", { error })
      throw new Error(error.message)
    }

    if (!newInvoiceId) {
      this.log.error("Error creating resource invoice", { newInvoiceId })
      throw new Error("Error creating resource invoice")
    }

    return newInvoiceId
  }

  async update(data: UpdateResourceInvoiceData) {
    const { error } = await this.supabase.rpc("update_resource_invoice", {
      p_resource_invoice_id: data.id,
      p_invoice_period_id: data.invoice_period_id ?? undefined,
      p_invoice_status: data.status ?? undefined,
      p_invoice_number: data.invoice_number ?? undefined,
      p_notes: data.notes ?? undefined,
      p_invoice_items: Object.values(data.items),
    })

    if (error) {
      this.log.error("Error updating resource invoice", { error })
      throw new Error(error.message)
    }

    return data
  }

  async submit(invoiceId: string) {
    const { error } = await this.supabase.rpc("submit_resource_invoice", {
      p_resource_invoice_id: invoiceId,
    })

    if (error) {
      this.log.error("Error submitting resource invoice", { error })
      throw new Error(error.message)
    }

    return true
  }

  async updateStatus(data: UpdateResourceInvoiceStatusData) {
    const { invoiceId, status } = data
    const { error } = await this.supabase.rpc(
      "update_resource_invoice_status",
      {
        p_resource_invoice_id: invoiceId,
        p_new_status: status,
      },
    )

    if (error) {
      this.log.error("Error updating invoice status", {
        error,
        invoiceId,
        status,
      })
      throw new Error(error.message)
    }

    return true
  }
}
